<template>
  <div>
    <div v-if="!ReadOnly" class="d-flex justify-content-end align-items-center">
      <ActionButton
        i="fas fa-file-plus"
        class="mr-2"
        variant="success"
        @click="addNewGoal"
      >
        Añadir Meta
      </ActionButton>
    </div>

    <SectionTitle :has-line="true" :content="showObjetivesSection ? 'Metas y Objetivos' : 'Metas'" />

    <Error
      v-if="hasGoalsError"
      :message="`Favor de agregar las metas${showObjetivesSection ? ' y objetivos': ''}${minRequiredGoals > 1 ? ` (debe contar con un mínimo de ${minRequiredGoals} Metas)`: ''}.`"
    />

    <Error
      v-if="showIncompleteNecessitySelectionError && !ReadOnly"
      message="Favor relacionar todas las necesidades a las metas."
    />
    
    <Error
      v-if="isIncompleteSubAreaSelection"
      message="Favor relacionar todas las sub áreas a las metas."
    />

    <div v-if="!addingNewGoal && !isEditingGoal" class="custom__bg-gray">
      <div
        v-for="(goal, index) in innerGoals"
        :key="goal.peiProgramServiceGoalId"
        class="container-fluid my-2 pt-4"
      >
        <div class="row">
          <div
            class="col-10 mt-3"
            :class="{ 'goal-title-container cursor-pointer': showObjetivesSection }"
            :data-toggle="showObjetivesSection ? 'collapse' : ''"
            :data-target="'#collapseGoal' + index"
            :aria-expanded="showObjetivesSection"
            :aria-controls="'collapseGoal' + index"
          >
            <div class="body-container">
              <div class="goal-title row align-items-center">
                <div class="gray-chip">
                  <b>Meta # {{ index + 1 }}</b>
                </div>
              </div>
              <div v-if="subAreaOptions.length > 0">
                <p class="content-text">
                  <b>Sub-Área:</b>
                  {{ goal.subAreaDesc }}
                </p>
              </div>
              <div>
                <p class="content-text">
                  {{ goal.spaLongDescription }}
                </p>
              </div>
              <div>
                <b>Fortaleza/Necesidad:</b>
                <p class="content-text">
                  {{ buildStrenghtsAndNecessity(goal) }}
                </p>
                <p
                  v-if="goal.strenghtsAndNecessities?.length > 1"
                  class="show-more__action"
                >
                  Ver Más
                </p>
              </div>
            </div>
          </div>
          <div v-if="!ReadOnly" class="dropdown col-2 text-right">
            <button
              id="ddmbGoalList"
              type="button"
              class="btn btn-dropdown-actions dropdown-toggle"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              Acciones
            </button>
            <div class="dropdown-menu" aria-labelledby="ddmbGoalList">
              <a class="dropdown-item" @click="editGoal(goal)">
                <i class="fa fa-pencil text-green pr-1" />
                Editar
              </a>

              <a class="dropdown-item" @click="handleDeleteGoal(goal)">
                <i class="fa fa-times-circle text-red pr-1" />
                Eliminar
              </a>
            </div>
          </div>
        </div>

        <div :id="`collapseGoal${index}`" class="collapse show pb-3">
          <Error
            v-if="isEmptySkills(goal.strenghtsAndNecessities)"
            message="Favor relacionar las necesidades a la meta."
          />

          <NecessityStrengthGoalList
            v-if="goal.strenghtsAndNecessities?.length > 1"
            title="Todas las Fortalezas y Necesidades"
            class="necessity-strength-goal-list__container px-4"
            :list="goal.strenghtsAndNecessities"
            :read-only="ReadOnly"
            @delete="handleDeleteNec($event, goal)"
          />

          <div v-if="showObjetivesSection" class="body-container collapse show">
            <div v-if="!ReadOnly">
              <hr class="separator" />
              <Error
                v-if="hasError && goal.peiProgramServiceObjective?.length <= 0"
                message="Favor documentar los objetivos."
              />
              <div
                v-if="goal.peiProgramServiceObjective?.length > 0"
                class="gray-chip mt-4"
              >
                <b>Objetivos:</b>
              </div>
              <div
                v-for="(objective, indexObj) in goal.peiProgramServiceObjective"
                :key="objective.peiProgramServiceObjectiveId"
                class="container-fluid my-2"
              >
                <div class="row">
                  <div class="col-10 mt-3">
                    <div class="row">
                      <p class="content-text maxHeigthLetters">
                        <b>{{ index + 1 }}.{{ indexObj + 1 }}</b>
                        {{ getMeasure(objective) }}
                      </p>
                    </div>
                  </div>
                  <div v-if="!ReadOnly" class="dropdown col-2 text-right pr-0">
                    <button
                      :id="'dropdownMenuButtonObj' + indexObj"
                      type="button"
                      class="btn btn-dropdown-actions dropdown-toggle"
                      data-toggle="dropdown"
                      aria-haspopup="true"
                      aria-expanded="true"
                    >
                      Acciones
                    </button>
                    <div
                      class="dropdown-menu"
                      :aria-labelledby="'dropdownMenuButtonObj' + indexObj"
                    >
                      <a
                        class="dropdown-item"
                        @click="editObjective(indexObj, index)"
                      >
                        <i class="fa fa-pencil text-green pr-1" />
                        Editar
                      </a>
                      <a
                        class="dropdown-item"
                        @click="handleDeleteObjective(objective, index)"
                      >
                        <i class="fa fa-times-circle text-red pr-1" />
                        Eliminar
                      </a>
                    </div>
                  </div>
                </div>
                <div v-if="showObjetivesSection && objective.isEditing">
                  <ObjectiveForm
                    :id="`objFormEdit-${goal.peiProgramServiceGoalId}`"
                    class="edit-obj-form"
                    :goal="goal"
                    :is-editing="true"
                    :objective="currentObjective"
                    :read-only="ReadOnly"
                    @cancel-objective="clearObjective(indexObj, index)"
                    @add-objetive="handleAddObjetive($event, index)"
                  />
                </div>
              </div>
              <div v-if="showObjetivesSection && !isEditingObjective">
                <ObjectiveForm
                  :ref="`objFormCreate-${goal.peiProgramServiceGoalId}`"
                  :id="`objFormCreate-${goal.peiProgramServiceGoalId}`"
                  :key="objFormKey"
                  :goal="goal"
                  :objective="currentObjective"
                  :read-only="ReadOnly"
                  @pending-change="setObjetivePendingChange(`objFormCreate-${goal.peiProgramServiceGoalId}`, $event)"
                  @cancel-objective="clearObjective(-1, index)"
                  @add-objetive="handleAddObjetive($event, index)"
                />
              </div>
              <div class="dropdown-divider" />
            </div>
          </div>
          <hr v-else style="border: 1px solid #b8c3bc;"/>
        </div>
      </div>
    </div>

    <div v-if="addingNewGoal" class="container-fluid custom__bg-gray py-2">
      <GoalForm
        ref="newGoalFormV1"
        :goal="newGoal"
        :pei-id="peiId"
        :program-service-area-id="parseInt(serviceAreaId)"
        :area-id="areaId"
        :sub-area-options="subAreaOptions"
        :selected-sub-areas="selectedSubAreas"
        :necessity-options="necessityOptions"
        :skills-required="skillsRequired"
        :read-only="ReadOnly"
        :adequacy-options="adequacyOptions"
        :show-adequacy="showAdequacy"
        @cancel-goal="resetState"
        @create-goal="handleAddGoal"
        @pending-change="hasPendingGoal = $event"
      />
    </div>

    <div v-if="isEditingGoal" class="container-fluid custom__bg-gray py-2">
      <GoalForm
        :goal="currentGoal"
        is-editing
        :pei-id="peiId"
        :program-service-area-id="parseInt(serviceAreaId)"
        :area-id="areaId"
        :sub-area-options="subAreaOptions"
        :selected-sub-areas="selectedSubAreas"
        :necessity-options="necessityOptions"
        :skills-required="skillsRequired"
        :read-only="ReadOnly"
        @cancel-goal="resetState"
        @update-goal="handleUpdateGoal"
      />
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import ObjectiveForm from "./ObjectiveForm.vue";
import GoalForm from "./GoalForm.vue";
import alert from "@/mixin/alert.mixin";
import loading from "@/mixin/loading.mixin";
import peiGoalMeasures from "@/utils/constants/pei/peiGoalMeasures";
import NecessityStrengthGoalList from "../NecessityStrengthGoalList";
import { SectionTitle } from "@/components/pei/shared";
import ActionButton from "ActionButton";
import Error from "@/components/common/Error";

import { createNamespacedHelpers } from "vuex";
import { modulesName } from "@/modules";
const { mapState, mapGetters } = createNamespacedHelpers(
  modulesName.programServiceModuleName
);

export default {
  mixins: [alert, loading],
  name: "GoalsList",
  components: {
    GoalForm,
    ActionButton,
    SectionTitle,
    ObjectiveForm,
    NecessityStrengthGoalList,
    Error
  },
  data() {
    return {
      isEditingGoal: false,
      isEditingObjective: false,
      addingNewGoal: true,
      currentGoal: null,
      currentObjective: null,
      newObjective: null,
      newGoal: {},
      objFormKey: Math.random(),
      goalFormKey: Math.random(),
      innerGoals: [],
      hasPendingGoal: false,
      pendingObjetives: []
    };
  },
  props: {
    areaId: {
      type: Number,
      default: 0
    },
    serviceAreaId: {
      type: [String, Number],
      default: null
    },
    peiId: {
      type: String,
      default: ""
    },
    hasError: {
      type: Boolean,
      default: false
    },
    minRequiredGoals: {
      type: [String, Number],
      default: 1
    },
    subAreaOptions: {
      type: Array,
      default: () => []
    },
    necessityOptions: {
      type: Array,
      default: () => []
    },
    skillsRequired: {
      type: Boolean,
      default: true
    },
    ReadOnly: {
      type: Boolean,
      default: false
    },
    showObjetivesSection: {
      type: Boolean,
      default: true
    },
    adequacyOptions: {
      type: Array,
      default: () => ([])
    },
    showAdequacy: {
      type: Boolean,
      default: false
    },
  },
  computed: {
    ...mapState(["selectedNecessities", "createdGoals"]),
    ...mapGetters(["resource", "selectedSubAreas", "isIncompleteNecessitySelection"]),
    hasGoalsError() {
      const currentCount = this.createdGoals?.length ?? 0;
      return this.hasError && currentCount < this.minRequiredGoals;
    },
    showIncompleteNecessitySelectionError() {
      return !this.hasGoalsError && this.hasError && this.isIncompleteNecessitySelection;
    },
    isIncompleteSubAreaSelection() {
      const usedSubAreaIds = this.createdGoals.map(x => x.subAreaId);
      const isIncomplete = this.selectedSubAreas.some(
        x => !usedSubAreaIds.includes(x.id)
      );

      return !this.hasGoalsError && this.hasError && isIncomplete;
    },
    havePendingChanges() {
      return this.hasPendingGoal || !!this.pendingObjetives.length;
    },
  },
  methods: {
    isEmptySkills(skills) {
      return this.skillsRequired && this.hasError && skills?.length == 0;
    },
    handleAddGoal(data) {
      this.$emit("create-goal", data);
      this.resetState();
    },
    handleUpdateGoal(data) {
      this.$emit("update-goal", data);
      this.resetState();
    },
    handleAddObjetive(data, index) {
      this.$emit("add-objetive", data);
      this.clearObjective(-1, index);
      this.resetState();
    },
    addNewGoal() {
      this.newGoal = {};
      this.addingNewGoal = true;
    },
    buildStrenghtsAndNecessity(goal) {
      return goal.strenghtsAndNecessities.find(x => !!x.name)?.name ?? "N/A";
    },
    editGoal(goal) {
      this.currentGoal = goal;
      this.isEditingGoal = true;
    },
    resetState() {
      this.goalFormKey = Math.random();
      this.objFormKey = Math.random();
      this.isEditingGoal = false;
      this.isEditingObjective = false;
      this.addingNewGoal = this.innerGoals.length == 0;
      this.newGoal = {};
      this.currentGoal = {};
    },
    async handleDeleteGoal(goal) {
      if (!goal || !goal?.peiProgramServiceGoalId) return;

      let confirmDeletion = await this.ShowCancelModal(
        "Atención",
        `${this.resource("R90016").replace(
          "</meta>",
          goal.spaShortDescription ? goal.spaShortDescription : ""
        )}`,
        "Si",
        "No"
      );

      if (!confirmDeletion) return;

      this.$emit("delete-goal", goal);
      this.resetState();
    },
    async handleDeleteNec(nec, goal) {
      if (!nec || !nec.id) return;

      let confirmDeletion = await this.ShowCancelModal(
        "Atención",
        `¿Está seguro que desea eliminar la ${
          nec.isStrength ? "Fortaleza" : "Necesidad"
        }?`,
        "Si",
        "No"
      );

      if (!confirmDeletion) return;

      const payload = {
        necessityId: nec.id,
        isStrength: nec.isStrength,
        goalIds: [goal.peiProgramServiceGoalId]
      };
      this.$emit("delete-goal-necessity", payload);
      this.resetState();
    },
    async handleDeleteObjective(obj, index) {
      this.currentGoal = this.innerGoals[index];
      this.isEditingObjective = false;

      if (!obj || !obj?.peiProgramServiceObjectivelId) return;

      let confirmDeletion = await this.ShowCancelModal(
        "Atención",
        `${this.resource("R90017").replace(
          "</objetivo>",
          obj.objectiveDescription ? obj.objectiveDescription : ""
        )}`,
        "Si",
        "No"
      );

      if (!confirmDeletion) return;

      this.$emit("delete-objetive", obj.peiProgramServiceObjectivelId);
      this.resetState();
    },
    clearObjective(indexObj, index) {
      this.objFormKey = Math.random();
      if (indexObj >= 0) {
        let goal = this.innerGoals[index];
        goal.peiProgramServiceObjective[indexObj].isEditing = false;
        Vue.set(this.innerGoals, index, goal);
      }
      this.isEditingObjective = false;
      this.currentObjective = {};
    },
    editObjective(objIndex, index) {
      let goal = this.innerGoals[index];
      goal.peiProgramServiceObjective[objIndex].isEditing = true;
      this.isEditingObjective = true;
      const obj = goal.peiProgramServiceObjective[objIndex];
      Vue.set(this.innerGoals, index, goal);
      this.currentObjective = obj
        ? obj
        : {
            peiProgramServiceGoalId: goal.peiProgramServiceGoalId
          };
      this.currentGoal = goal;

      for (let i = 0; i < goal.peiProgramServiceObjective.length; i++) {
        if (i != index) {
          document.getElementById(`collapseGoal${i}`)
            ? document
                .getElementById(`collapseGoal${i}`)
                .classList.remove("show")
            : null;
        }
      }
    },
    getMeasure(objective) {
      let result = objective.objectiveDescription;
      result = result.endsWith(".") ? result : `${result}.`;
      switch (objective.peiObjectiveMeasureCode) {
        case peiGoalMeasures.attempts:
          result += ` En ${objective.measure1Amt} de ${objective.measure2Amt} intentos.`;
          break;
        case peiGoalMeasures.percentual:
          result += ` En el ${objective.measure1Amt}% de las veces.`;
          break;
        default:
          result += "";
          break;
      }
      return result;
    },
    async save() {
      let isValid = true;
      
      if (this.hasPendingGoal) {
        const goalValid = await this.$refs.newGoalFormV1.save();
        isValid = isValid && goalValid;
      }

      if (!!this.pendingObjetives.length) {
        const objetivePromises = [];

        for (let index = 0; index < this.pendingObjetives.length; index++) {
          objetivePromises.push(this.$refs[this.pendingObjetives[index]][0].save());
        }

        const results = await Promise.all(objetivePromises);
        isValid = isValid && results.every(value => !!value);
      }

      return isValid;
    },
    setObjetivePendingChange(ref, hasPendingChange) {
      this.pendingObjetives = this.pendingObjetives.filter(x => x != ref);

      if (hasPendingChange) {
        this.pendingObjetives.push(ref);
      }
    }
  },
  watch: {
    createdGoals: {
      deep: true,
      immediate: true,
      handler(newValue) {
        this.innerGoals = [...newValue];
        this.resetState();
      }
    },
    havePendingChanges(newVal) {
      this.$emit('pending-changes', newVal);
    }
  }
};
</script>

<style scoped>
.separator {
  border-bottom: 1px solid #e2e2e2;
  border-top: 0;
  margin: 0;
}
.edit-obj-form {
  margin: 0 -15px;
}
.body-container {
  margin-left: 39px;
}
.gray-chip {
  background-color: #b5c3bc;
  border: 1px solid #b5c3bc;
  border-radius: 15px;
  color: white;
  font-size: 16px;
  line-height: 24px;
  padding: 0 15px;
  width: fit-content;
}

.goal-title-container[aria-expanded="true"]:before {
  font-family: "Font Awesome 5 Pro";
  content: "\f107";
  font-size: 26px;
  color: #2db782;
  float: left;
}
.goal-title-container:before {
  font-family: "Font Awesome 5 Pro";
  content: "\f105";
  font-size: 26px;
  color: #2db782;
  float: left;
}
.goal-title {
  height: 39px;
  margin-left: 0;
}
.content-text {
  font-size: 16px;
  font-weight: normal;
  line-height: 24px;
}
.btn-dropdown-actions {
  text-align: center;
  font-family: "Lato", sans-serif;
  font-weight: 700;
  font-size: 1rem;
  color: rgb(12, 19, 16);
  border-radius: 4px;
  margin-right: 0px;
  border: 1px solid #2db782;
  width: 7.8125rem;
  height: 2.25rem;
}

.maxHeigthLetters {
  word-wrap: break-word;
  width: 100%;
}

.show-more__action {
  cursor: pointer;
  color: #2db782;
  text-decoration: underline #2db782;
}

.necessity-strength-goal-list__container {
  margin-left: 40px;
}

.custom__bg-gray {
  background-color: #f7f7f7 !important;
}
</style>
